import { getApi } from "./resonanceApi";
import { RichContentBlock } from "../types/richContent";

export type VideoMetaFields = {
  title?: string;
  description?: string;
  tags?: string; // comma-separated from backend
  category?: string;
  linked_uid?: string;
  linked_url?: string;
  name?: string;
  [key: string]: any;
};

export type VideoMeta = {
  uid: string;
  hlsUrl: string;
  thumbnailUrl: string;
  duration?: number | null;
  created?: string | null;
  meta?: VideoMetaFields;
};

export type VideosListResponse = {
  items: VideoMeta[];
  nextCursor: string | null;
};

export type VideoContextResponse = {
  current: VideoMeta;
  items: VideoMeta[];
  index: number;
  total: number;
  nextCursor: string | null;
  direction?: "next" | "prev";
};

export async function getVideos(params?: {
  tags?: string[];
  search?: string;
  limit?: number;
  cursor?: string;
}) {
  const sp = new URLSearchParams();

  if (params?.tags?.length) {
    sp.set("tags", params.tags.join(","));
  }

  if (params?.search?.trim()) {
    sp.set("search", params.search.trim());
  }

  if (params?.limit) {
    sp.set("limit", String(params.limit));
  }

  if (params?.cursor) {
    sp.set("cursor", params.cursor);
  }

  const queryString = sp.toString();
  const url = queryString ? `videos?${queryString}` : "videos";

  const { data } = await getApi<VideosListResponse>(url);
  return data;
}

export async function getVideo(id: string) {
  const { data } = await getApi<VideoMeta>(`videos/${encodeURIComponent(id)}`);
  return data;
}

/**
 * Fetch an explicit set of videos in ONE request (the videoCards content block).
 * The API applies the same randomisation-arm scoping as the Videos tab, so a video the
 * member's arm excludes simply does not come back — callers must tolerate a short list.
 * Returned in list order; callers that care re-order by their own uid list.
 */
export async function getVideosByUids(uids: string[]) {
  const wanted = uids.filter(Boolean);
  if (!wanted.length) return [];

  const { data } = await getApi<VideosListResponse>(
    `videos?${new URLSearchParams({ uids: wanted.join(",") }).toString()}`
  );
  return data?.items ?? [];
}

/**
 * The video's authored description: the same rich blocks as an article (text, images,
 * cards, links, other videos…), resolved for the requested language. `meta.description`
 * on the video itself is only the one-line teaser derived from these blocks.
 *
 * `content_id: null` with empty blocks means nothing was authored — a normal state.
 */
export type VideoDescriptionResponse = {
  content_id: number | null;
  content: { blocks: RichContentBlock[] };
};

export async function getVideoDescription(
  studyId: number,
  videoUid: string,
  lang: string
) {
  const { data } = await getApi<VideoDescriptionResponse>(
    "studies/{studyId}/videos/{videoUid}/description",
    { studyId, videoUid },
    { lang }
  );
  return data;
}

/**
 * Fetch a page of videos relative to an anchor `id`.
 * - direction "next" (default): items after `id`
 * - direction "prev": items before `id` (returned in scroll-ready order)
 */
export async function getVideoContext(
  id: string,
  params?: {
    tags?: string[];
    search?: string;
    limit?: number;
    direction?: "next" | "prev";
  }
) {
  const sp = new URLSearchParams({ current: id });

  if (params?.tags?.length) {
    sp.set("tags", params.tags.join(","));
  }

  if (params?.search?.trim()) {
    sp.set("search", params.search.trim());
  }

  if (params?.limit) {
    sp.set("limit", String(params.limit));
  }

  if (params?.direction) {
    sp.set("direction", params.direction);
  }

  const { data } = await getApi<VideoContextResponse>(
    `videos/context?${sp.toString()}`
  );
  return data;
}

export const getNextFrom = (
  id: string,
  p?: { tags?: string[]; search?: string; limit?: number }
) => getVideoContext(id, { ...p, direction: "next" });

export const getPrevFrom = (
  id: string,
  p?: { tags?: string[]; search?: string; limit?: number }
) => getVideoContext(id, { ...p, direction: "prev" });